import { VisibilityElement } from './visibility'

/** Slides are `x-pinch-zoom` elements, which expose a reset for their zoom state. */
type ZoomableSlide = HTMLElement & { reset?: () => void }

interface Selectors {
  readonly gallery: string;
  readonly container: string;
  readonly buttons: string;
  readonly globalTriggers: string;
  readonly close: string;
}

/**
 * How many slides either side of the active one keep their sources attached.
 * Every slide is viewport-sized, so leaving a whole gallery attached can OOM a
 * mobile renderer. One neighbour each way keeps swiping seamless.
 */
const SLIDE_WINDOW = 1

/**
 * LightBoxElement - Custom Element
 * Extends VisibilityElement.
 *
 * Opens a fullscreen product media gallery. Triggers (data-role="lightbox-trigger")
 * open the lightbox and scroll to the matching image; the close control hides it.
 *
 * Layout is driven by CSS: desktop stacks images vertically (browse by scrolling),
 * mobile lays them out as a full-screen horizontal snap carousel. The only scripted
 * navigation is the mobile prev/next buttons, which scroll the gallery by one slide.
 *
 * This element also owns image loading. Slides render with `manual_load`, so
 * vanilla-lazyload ignores them and only the active slide plus SLIDE_WINDOW
 * neighbours ever hold real sources; everything else is returned to its placeholder.
 * Zoom lives in the `x-pinch-zoom` slides and is reset whenever a slide scrolls away.
 */
class LightBoxElement extends VisibilityElement {
  private readonly selectors: Selectors
  private slides: HTMLElement[] = []
  private placeholders = new WeakMap<HTMLImageElement, string>()
  private activeObserver: IntersectionObserver | null = null
  private activeIndex = 0
  private ratios = new Map<Element, number>()

  constructor() {
    super()

    this.selectors = {
      gallery: '[data-role="gallery"]',
      container: '[data-role="gallery_image_container"]',
      buttons: 'button[data-role="gallery-button"]',
      globalTriggers: 'div[data-role="lightbox-trigger"]',
      close: '[data-role="close"], .lightbox-image'
    } as const

    // VisibilityElement assigns these as instance properties, so wrap rather than
    // override. Sources attach only while the lightbox is open: the container is
    // `opacity-0` rather than hidden, so an always-on window would eagerly decode
    // the first slide on every PDP whether or not anyone opens the gallery.
    const baseShow = this.handleShow
    this.handleShow = () => {
      baseShow()
      this.applyWindow(this.activeIndex)
    }

    const baseHide = this.handleHide
    this.handleHide = () => {
      baseHide()
      this.detachAll()
    }
  }

  private get isOpen(): boolean {
    return this.dataset.ui === 'visible'
  }

  private imageFor(slide: HTMLElement): HTMLImageElement | null {
    return slide.querySelector<HTMLImageElement>('img.lightbox-image')
  }

  /**
   * Caches the slide list and each image's placeholder src, so detached slides
   * can be returned to exactly the markup Liquid rendered.
   */
  private collectSlides(): void {
    this.slides = Array.from(this.querySelectorAll<HTMLElement>(this.selectors.container))

    this.slides.forEach((slide) => {
      const img = this.imageFor(slide)
      if (img && !this.placeholders.has(img)) {
        this.placeholders.set(img, img.getAttribute('src') || '')
      }
    })
  }

  private attach(img: HTMLImageElement): void {
    const { src, srcset, sizes } = img.dataset
    if (!src || img.dataset.attached === 'true') return

    if (sizes) img.sizes = sizes
    if (srcset) img.srcset = srcset
    img.src = src
    img.dataset.attached = 'true'
  }

  private detach(img: HTMLImageElement): void {
    if (img.dataset.attached !== 'true') return

    img.removeAttribute('srcset')
    img.removeAttribute('sizes')
    img.src = this.placeholders.get(img) || ''
    delete img.dataset.attached
  }

  /**
   * Attaches the active slide and its neighbours, detaching everything else.
   */
  private applyWindow(index: number): void {
    if (!this.isOpen) return

    this.slides.forEach((slide, i) => {
      // A slide scrolled away while zoomed would keep both its transform and its
      // compositor layer, so hand it back at 1x.
      if (i !== index) (slide as ZoomableSlide).reset?.()

      const img = this.imageFor(slide)
      if (!img) return

      if (Math.abs(i - index) <= SLIDE_WINDOW) this.attach(img)
      else this.detach(img)
    })
  }

  private detachAll(): void {
    this.slides.forEach((slide) => {
      (slide as ZoomableSlide).reset?.()

      const img = this.imageFor(slide)
      if (img) this.detach(img)
    })
  }

  /**
   * Tracks the active slide inside the gallery scroller. The same observer covers
   * the mobile horizontal carousel and the desktop vertical stack.
   */
  private observeActiveSlide(): void {
    const gallery = this.querySelector<HTMLElement>(this.selectors.gallery)
    if (!gallery || !this.slides.length) return

    this.activeObserver = new IntersectionObserver(
      (entries) => {
        // `isIntersecting` stays true for a slide that is still 40% visible, so the
        // active slide is whichever currently covers the most of the scroller, not
        // whichever happened to fire. Ratios are accumulated across callbacks.
        entries.forEach((entry) => this.ratios.set(entry.target, entry.intersectionRatio))

        let bestIndex = this.activeIndex
        let bestRatio = -1

        this.slides.forEach((slide, i) => {
          const ratio = this.ratios.get(slide) ?? 0
          if (ratio > bestRatio) {
            bestRatio = ratio
            bestIndex = i
          }
        })

        if (bestRatio <= 0 || bestIndex === this.activeIndex) return

        this.activeIndex = bestIndex
        this.applyWindow(bestIndex)
      },
      { root: gallery, threshold: [0, 0.25, 0.5, 0.75, 1] }
    )

    this.slides.forEach((slide) => this.activeObserver?.observe(slide))
  }

  /**
   * Scrolls the gallery to the image matching the given 1-based index.
   * Works for both axes, so it covers the vertical (desktop) and horizontal
   * (mobile) layouts without branching.
   */
  private scrollToImage(index: string): void {
    const gallery = this.querySelector<HTMLElement>(this.selectors.gallery)
    const target = this.querySelector<HTMLElement>(`${this.selectors.container}[data-index="${index}"]`)
    if (!gallery || !target) return

    // Not `scrollIntoView`: it scrolls every scrollable ancestor including the
    // document, and the slides sit inside a `position: fixed` overlay that never
    // moves relative to the viewport — so the page behind the overlay gets yanked
    // to the bottom of the PDP. Only the gallery scrollport should move.
    const galleryRect = gallery.getBoundingClientRect()
    const targetRect = target.getBoundingClientRect()

    gallery.scrollTo({
      top: gallery.scrollTop + (targetRect.top - galleryRect.top),
      left: gallery.scrollLeft + (targetRect.left - galleryRect.left)
    })
  }

  /**
   * Opens the lightbox and scrolls to the triggered image.
   */
  private handleGlobalTrigger = (e: Event): void => {
    const index = (e.currentTarget as HTMLElement).dataset.index

    // data-index is Liquid's 1-based forloop.index. Set the active slide before
    // opening so handleShow attaches the right window straight away.
    if (index) this.activeIndex = Math.max(0, Number(index) - 1)

    this.handleToggle()
    if (index) this.scrollToImage(index)
  }

  /**
   * Mobile prev/next: scrolls the gallery horizontally by one slide.
   */
  private handleButton = (e: Event): void => {
    const button = (e.target as HTMLElement).closest<HTMLButtonElement>(this.selectors.buttons)
    const gallery = this.querySelector<HTMLElement>(this.selectors.gallery)
    if (!button || !gallery) return
    const direction = button.dataset.action === 'next' ? 1 : -1
    gallery.scrollBy({ left: direction * gallery.clientWidth, behavior: 'smooth' })
  }

  /**
   * Closes the lightbox.
   */
  private handleClose = (): void => {
    this.handleHide()
  }

  addEventListeners(): void {
    super.addEventListeners()

    this.querySelectorAll<HTMLElement>(this.selectors.buttons)
      .forEach(el => el.addEventListener('click', this.handleButton))

    document.querySelectorAll<HTMLElement>(this.selectors.globalTriggers)
      .forEach(el => el.addEventListener('click', this.handleGlobalTrigger))

    document.querySelectorAll<HTMLElement>(this.selectors.close)
      .forEach(el => el.addEventListener('click', this.handleClose))
  }

  removeEventListeners(): void {
    super.removeEventListeners()

    this.querySelectorAll<HTMLElement>(this.selectors.buttons)
      .forEach(el => el.removeEventListener('click', this.handleButton))

    document.querySelectorAll<HTMLElement>(this.selectors.globalTriggers)
      .forEach(el => el.removeEventListener('click', this.handleGlobalTrigger))

    document.querySelectorAll<HTMLElement>(this.selectors.close)
      .forEach(el => el.removeEventListener('click', this.handleClose))
  }

  connectedCallback(): void {
    // `super.connectedCallback()` rather than a bare `this.addEventListeners()`: the
    // base class calls that for us AND collects the overlay panels it hides while
    // closed. `addEventListeners` is safe to reach twice — every handler is a stable
    // instance property, so `addEventListener` de-duplicates.
    super.connectedCallback()
    this.collectSlides()
    this.observeActiveSlide()
  }

  disconnectedCallback(): void {
    // Routed through super so the base class clears its panel-hide timeout and
    // restores any panel it left hidden.
    super.disconnectedCallback()
    this.detachAll()
    this.activeObserver?.disconnect()
    this.activeObserver = null
    this.ratios.clear()
  }
}

export { LightBoxElement }
